import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useJobs } from '@/hooks/useJobs';
import { useDashboardMetrics } from '@/hooks/useReports';
import { JobStatus } from '@recruitai/shared';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { StageOwnerDashboard } from '@/components/pipeline/StageOwnerDashboard';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const STATUS_COLORS: Record<string, string> = {
  pending: '#94a3b8',
  analyzing: '#60a5fa',
  approved: '#34d399',
  rejected: '#f87171',
  interviewed: '#a78bfa',
  hired: '#fbbf24',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  analyzing: 'Analisando',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  interviewed: 'Entrevistado',
  hired: 'Contratado',
};

function getDefaultDates() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { jobs, isLoading } = useJobs();
  const defaults = getDefaultDates();
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(defaults.startDate, defaults.endDate);

  const openJobs = jobs.filter(j => j.status === JobStatus.OPEN).length;
  const draftJobs = jobs.filter(j => j.status === JobStatus.DRAFT).length;
  const closedJobs = jobs.filter(j => j.status === JobStatus.CLOSED).length;
  const totalCandidates = jobs.reduce((sum, j) => sum + j.candidateCount, 0);

  const pieData = metrics ? Object.entries(metrics.candidatesByStatus)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({ name: STATUS_LABELS[key] || key, value, color: STATUS_COLORS[key] || '#cbd5e1' })) : [];

  const weeklyData = metrics?.candidatesByWeek?.map(w => ({ semana: w.week.slice(5), candidatos: w.count })) || [];

  const topJobsData = metrics?.topJobs?.map(j => ({ nome: j.title.length > 20 ? j.title.slice(0, 20) + '…' : j.title, candidatos: j.candidateCount })) || [];
  
  // Get recent jobs (last 3)
  const recentJobs = [...jobs]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMins < 60) return `Há ${diffInMins} minuto${diffInMins !== 1 ? 's' : ''}`;
    if (diffInHours < 24) return `Há ${diffInHours} hora${diffInHours !== 1 ? 's' : ''}`;
    return `Há ${diffInDays} dia${diffInDays !== 1 ? 's' : ''}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2 font-heading">{getGreeting()}, {user?.name?.split(' ')[0]} 👋</h2>
          <p className="text-muted-foreground">
            Aqui está o resumo da sua plataforma TalentumIQ
          </p>
        </div>

        {/* Stats Cards */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregando estatísticas...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardDescription>📋 Vagas Abertas</CardDescription>
                  <CardTitle className="text-4xl font-heading">{openJobs}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    {draftJobs} rascunho{draftJobs !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardDescription>👥 Total de Candidatos</CardDescription>
                  <CardTitle className="text-4xl font-heading">{totalCandidates}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    Em {jobs.length} vaga{jobs.length !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardDescription>✅ Vagas Fechadas</CardDescription>
                  <CardTitle className="text-4xl font-heading">{closedJobs}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    Processos concluídos
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardDescription>📊 Total de Vagas</CardDescription>
                  <CardTitle className="text-4xl font-heading">{jobs.length}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    Na plataforma
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Metrics Charts */}
            {metricsLoading ? (
              <div className="text-center py-6 mb-8">
                <p className="text-muted-foreground">Carregando métricas avançadas...</p>
              </div>
            ) : metrics && (
              <>
                {/* KPI row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <Card className="card-hover">
                    <CardHeader className="pb-2">
                      <CardDescription>⏱️ Tempo Médio de Contratação</CardDescription>
                      <CardTitle className="text-3xl font-heading">{metrics.averageHiringTime.toFixed(1)} <span className="text-base font-normal text-muted-foreground">dias</span></CardTitle>
                    </CardHeader>
                  </Card>
                  <Card className="card-hover">
                    <CardHeader className="pb-2">
                      <CardDescription>✅ Taxa de Aprovação</CardDescription>
                      <CardTitle className="text-3xl font-heading">{metrics.approvalRate.toFixed(1)}%</CardTitle>
                    </CardHeader>
                  </Card>
                  <Card className="card-hover">
                    <CardHeader className="pb-2">
                      <CardDescription>🎤 Total de Entrevistas</CardDescription>
                      <CardTitle className="text-3xl font-heading">{metrics.totalInterviews}</CardTitle>
                    </CardHeader>
                  </Card>
                </div>

                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Pie: Candidatos por Status */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Candidatos por Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {pieData.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Sem dados</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>

                  {/* Area: Tendência Semanal */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Candidatos por Semana</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {weeklyData.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Sem dados</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <AreaChart data={weeklyData}>
                            <XAxis dataKey="semana" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Area type="monotone" dataKey="candidatos" stroke="#00C2C7" fill="#00C2C7" fillOpacity={0.15} />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Bar: Top Vagas */}
                {topJobsData.length > 0 && (
                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle className="text-base">Top Vagas por Candidatos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={topJobsData} layout="vertical">
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                          <YAxis type="category" dataKey="nome" width={150} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="candidatos" fill="#00C2C7" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Vagas Recentes</CardTitle>
                <CardDescription>
                  Últimas vagas criadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recentJobs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    Nenhuma vaga criada ainda
                  </p>
                ) : (
                  <div className="space-y-4">
                    {recentJobs.map((job, index) => (
                      <div 
                        key={job.jobId} 
                        className={`flex items-center justify-between ${index < recentJobs.length - 1 ? 'border-b pb-4' : ''}`}
                      >
                        <div className="flex-1 cursor-pointer" onClick={() => navigate(`/jobs/${job.jobId}`)}>
                          <p className="font-medium hover:text-primary">{job.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {job.candidateCount} candidato{job.candidateCount !== 1 ? 's' : ''} • Status: {
                              job.status === JobStatus.OPEN ? 'Aberta' :
                              job.status === JobStatus.DRAFT ? 'Rascunho' :
                              'Fechada'
                            }
                          </p>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {formatTimeAgo(job.createdAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Stage Owner Dashboard */}
        <div className="mt-6">
          <StageOwnerDashboard />
        </div>
      </div>
    </AppLayout>
  );
}
